import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { ButtonCss, Header } from '../PaymentModal.styled'
import { WalletInfoImage } from '@Components/Layout/Header/Assets/images'
import { useGetUserProfileQuery } from '@Features/Dashboard/DashboardSlice/Dashboard.slice'
import { usePaymentCheckout } from '../usePaymentDispatch'
import { useTranslation } from 'react-i18next'
import { trackEvent } from '@Utilities/marketingTags'
import { PromocodeButton } from './PromocodeButton'
import Spacer from '@eduact/ed-components/Spacer'
import { Typography } from '@eduact/ed-system'
import EdTypography from '@Components/Utilities/EdTypography/EdTypography'
import routes from '@Constants/Routes'
import usePlatformFlags from '@Hooks/usePlatformFlags'
import type { PaymentModalVariant } from '../PaymentModal'

type Props = {
	setActiveSection: any
	courseId: any
	setShowPaymentPopup: any
	onSuccess: any
	onReject: any
	price: any
	classroomName: any
	variant?: PaymentModalVariant
}
const WalletPayment: React.FC<Props> = ({
	setActiveSection,
	courseId,
	setShowPaymentPopup,
	onSuccess,
	onReject,
	price,
	classroomName,
	variant = 'course',
}) => {
	const userProfile = useGetUserProfileQuery()
	// The variant → endpoint/id-field mapping lives in one shared hook.
	const { checkout, state: checkoutData } = usePaymentCheckout(variant, courseId)
	const navigate = useNavigate()

	const { walletEnabled } = usePlatformFlags()

	const [discount, setDiscount] = useState<any>()
	const onApply = (value: any) => {
		setDiscount(value)
	}
	const submitHandler = () => checkout({ payment_method: 'wallet', promocode: discount?.code })
	const { t } = useTranslation(['payment'], { keyPrefix: 'wallet_payment' })
	const { t: tGeneric } = useTranslation(['generic'])

	// Insufficient balance: block the confirm and offer the recharge doorway
	// instead of a silent server rejection.
	const balance = userProfile.data?.data.student.wallet.amount ?? 0
	const due = Number(discount?.value ?? price)
	const insufficient = Number.isFinite(due) && due > balance
	const goRecharge = () => {
		setShowPaymentPopup(false)
		navigate(routes.activityWallet)
	}

	useEffect(() => {
		if (checkoutData.isSuccess) {
			// Wallet debits confirm synchronously, so this IS the purchase moment.
			// `due` is the promocode-adjusted amount — the old pixel reported the
			// undiscounted list price. (Old event name: 'Purchases'.)
			trackEvent('Purchase', {
				content_id: courseId,
				content_name: classroomName,
				content_type: variant,
				payment_method: 'wallet',
				currency: 'EGP',
				value: due,
			})
			onSuccess && onSuccess()
		}
	}, [checkoutData.isSuccess, onSuccess])

	useEffect(() => {
		if (checkoutData.isError) {
			trackEvent('PurchaseFailed', {
				content_id: courseId,
				content_type: variant,
				payment_method: 'wallet',
				currency: 'EGP',
				value: due,
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [checkoutData.isError])

	return (
		<Container>
			<Header>
				{t('title')} {classroomName} {t('for')} <Typography textDecoration={discount && 'line-through'}>{price}</Typography>{' '}
				{discount && <EdTypography color="primary">{discount.value}</EdTypography>} {tGeneric('egp')}
			</Header>
			<Spacer mb="2rem" />
			<PromocodeButton price={price} onApply={onApply} courseId={courseId} variant={variant} />
			<Spacer mb="2rem" />
			<ContentWrapper>
				<Image src={WalletInfoImage} alt="wallet image" />
				<ConfirmationWrapper>
					<Text mobileMargin="26px">
						{t('balance')} {userProfile.data?.data.student.wallet.amount} {tGeneric('egp')} {t('balance2')}
					</Text>
					{/* Only meaningful while the wallet product exists: with recharging
					    withdrawn there is nothing the student could do about it, so the
					    note goes the same way as the اشحن المحفظة doorway below. */}
					{insufficient && walletEnabled && (
						<InsufficientNote>
							{t('insufficient_balance', { defaultValue: 'رصيد محفظتك لا يكفي لإتمام العملية.' })}
						</InsufficientNote>
					)}
					<YesButton onClick={submitHandler} disabled={insufficient}>
						{t('yes')}
					</YesButton>
					{/* Paying from an existing balance still follows the product's own
					    payment_methods_allowed; only the recharge doorway belongs to
					    the wallet product, so it goes when that is switched off. */}
					{insufficient && walletEnabled && (
						<RechargeButton type="button" onClick={goRecharge}>
							{t('recharge_wallet', { defaultValue: 'اشحن المحفظة' })}
						</RechargeButton>
					)}
					<CancelButton onClick={() => setActiveSection('payment-choice')}>{t('cancel')}</CancelButton>
				</ConfirmationWrapper>
			</ContentWrapper>
		</Container>
	)
}

const Container = styled.div`
	button {
		cursor: pointer;
	}
`
const ContentWrapper = styled.div`
	display: flex;
	justify-content: space-between;
	padding-top: 26px;

	@media (max-width: 425px) {
		align-items: center;
		flex-direction: column;
	}
`

const Image = styled.img`
	width: 240px;
`

const ConfirmationWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
`

const Text = styled.p<{ mobileMargin: any }>`
	font-family: var(--font-sans);
	font-size: 12px;
	color: #9b9b9b;
	margin-bottom: 26px;

	@media (max-width: 425px) {
		margin-top: ${props => props?.mobileMargin};
	}
`

const InsufficientNote = styled.p`
	font: var(--fw-bold, 700) 12px var(--font-sans);
	color: var(--error-dark, #b91c1c);
	margin-bottom: 12px;
	text-align: center;
`

const YesButton = styled.button`
	${ButtonCss};
	background-color: #0655CB;
	margin-bottom: 10px;
	&:disabled {
		opacity: 0.55;
		cursor: not-allowed;
	}
`

const RechargeButton = styled.button`
	${ButtonCss};
	background-color: var(--navy, #1e3a8a);
	margin-bottom: 10px;
`

const CancelButton = styled.button`
	${ButtonCss};
	background-color: #ff8532;
`

export default WalletPayment
